import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import AxiosInstance from 'src/components/Axios';
import { invalidateCategoryScopes } from 'src/hooks/useCategoryScopes';
import {
	CForm, CFormLabel, CFormInput, CButton, CRow, CCol,
	CCard, CCardHeader, CCardBody, CFormCheck,
} from '@coreui/react';
import { CenterLoader } from 'src/components/myComponents/CenterLoader';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const urlApi = '/category-scopes';
const urlRouter = '/support-scopes';

const SupportScopesForm = ({ user_data }) => {
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const isCreateMode = !id;

	const [isEdit, setIsEdit] = useState(isCreateMode || location.pathname.includes('/update/'));
	useEffect(() => {
		setIsEdit(isCreateMode || location.pathname.includes('/update/'));
	}, [location.pathname, isCreateMode]);

	const [data, setData] = useState(null);
	const [code, setCode] = useState('');
	const [name, setName] = useState('');
	const [isActive, setIsActive] = useState(true);
	const [loading, setLoading] = useState(!!id);

	useEffect(() => {
		if (!id) return;
		setLoading(true);
		AxiosInstance.get(`${urlApi}/${id}`)
			.then(res => {
				const item = res.data;
				setData(item);
				setCode(item.code || '');
				setName(item.name || '');
				setIsActive(item.is_active ?? true);
			})
			.catch(() => toast.error('Помилка завантаження'))
			.finally(() => setLoading(false));
	}, [id]);

	const hasChanges = !isCreateMode && data !== null && (
		code !== (data.code || '') ||
		name !== (data.name || '') ||
		isActive !== (data.is_active ?? true)
	);

	const handleReset = useCallback(() => {
		if (!data) return;
		setCode(data.code || '');
		setName(data.name || '');
		setIsActive(data.is_active ?? true);
	}, [data]);

	const handleBack = useCallback(() => navigate(-1), [navigate]);

	const handleSubmit = async () => {
		if (!code.trim() || !name.trim()) {
			toast.warning("Код і назва обов'язкові");
			return;
		}
		const payload = { code: code.trim(), name: name.trim(), is_active: isActive };
		try {
			if (isCreateMode) {
				await AxiosInstance.post(urlApi, payload);
				invalidateCategoryScopes();
				toast.success('Створено!');
				navigate(urlRouter);
			} else {
				const res = await AxiosInstance.put(`${urlApi}/${id}`, payload);
				setData(res.data);
				invalidateCategoryScopes();
				toast.success('Оновлено!');
			}
		} catch (err) {
			toast.error(err.response?.data?.error || 'Помилка збереження');
		}
	};

	const viewFields = data ? [
		{ key: 'id', label: 'ID', value: String(data.id) },
		{ key: 'code', label: 'Код', value: data.code || '' },
		{ key: 'name', label: 'Назва', value: data.name || '' },
		{ key: 'is_active', label: 'Активна', value: data.is_active ? 'Так' : 'Ні' },
	] : [];

	const cardTitle = isCreateMode
		? 'Нова область застосування'
		: (isEdit ? 'Область застосування — редагування' : 'Область застосування — перегляд');

	return (
		<>
			<ToastContainer />
			<CCard className="mb-4">
				<CCardHeader>
					<strong>{cardTitle}</strong>
				</CCardHeader>
				<CCardBody>
					{loading ? (
						<CenterLoader />
					) : (
					<CForm className="mb-3">
						{isEdit ? (
							<CRow className="g-3">
								<CCol md={4}>
									<CFormLabel>Код *</CFormLabel>
									<CFormInput
										value={code}
										onChange={(e) => setCode(e.target.value)}
										placeholder="Наприклад: knowledge_base"
										autoComplete="off"
									/>
								</CCol>
								<CCol md={6}>
									<CFormLabel>Назва *</CFormLabel>
									<CFormInput
										value={name}
										onChange={(e) => setName(e.target.value)}
										placeholder="Наприклад: База знань"
										autoComplete="off"
									/>
								</CCol>
								<CCol md={2} className="d-flex align-items-end pb-1">
									<span title="Визначає, чи доступна ця область застосування для вибору при створенні/редагуванні категорій. Неактивна область прихована з усіх селектів.">
										<CFormCheck
											id="isActiveCheck"
											label="Активна"
											checked={isActive}
											onChange={(e) => setIsActive(e.target.checked)}
										/>
									</span>
								</CCol>
							</CRow>
						) : (
							<CRow className="g-3">
								{viewFields.map(({ key, label, value }) => (
									<CCol md={6} key={key}>
										<CFormLabel>{label}</CFormLabel>
										<CFormInput type="text" value={value} disabled readOnly />
									</CCol>
								))}
							</CRow>
						)}

						<div className="d-flex justify-content-start mt-4">
							<CButton color="secondary" variant="outline" onClick={handleBack} title="Повернутись до попередньої сторінки">
								← Назад
							</CButton>
							{!isEdit && (
								<CButton color="success" className="ms-2" onClick={() => navigate(`${urlRouter}/update/${id}`)} title="Перейти до форми редагування">
									Редагувати
								</CButton>
							)}
							{isEdit && (
								<CButton
									color="success"
									className="ms-2"
									onClick={handleSubmit}
									disabled={!isCreateMode && !hasChanges}
									title={isCreateMode ? 'Створити новий запис' : 'Зберегти зміни'}
								>
									{isCreateMode ? 'Створити' : 'Зберегти'}
								</CButton>
							)}
							{isEdit && !isCreateMode && (
								<CButton
									color="secondary"
									variant="outline"
									className="ms-2"
									onClick={handleReset}
									disabled={!hasChanges}
									title="Скинути форму до останніх збережених значень"
								>
									Скинути
								</CButton>
							)}
							{isEdit && !isCreateMode && (
								<CButton
									color="danger"
									variant="outline"
									className="ms-2"
									onClick={() => navigate(`${urlRouter}/view/${id}`)}
									title="Скасувати редагування і повернутись до перегляду"
								>
									До перегляду
								</CButton>
							)}
						</div>
					</CForm>
					)}
				</CCardBody>
			</CCard>
		</>
	);
};

export default SupportScopesForm;
